import { useState } from "react";
import { useOps } from "../store/ops";
import { useRH } from "../store/rh";
import { useTasks } from "../store/tasks";
import { PERSONAS, ALL_DEPTS } from "../data/org";
import { initials, monogramColor } from "../lib/monogram";
import { AgentChat } from "./AgentChat";

const STATUS_LABEL: Record<string, string> = {
  idle: "💤 ocioso",
  thinking: "🧠 pensando",
  typing: "✍️ digitando",
  drawing: "🎨 desenhando",
  reviewing: "👁 revisando",
  delivered: "✓ entregue",
  blocked: "✗ travado"
};

const STATUS_COLOR: Record<string, string> = {
  idle:      "bg-bone border-ink/20",
  thinking:  "bg-gold/30 border-gold animate-pulse",
  typing:    "bg-gold border-gold animate-pulse",
  drawing:   "bg-red/30 border-red animate-pulse",
  reviewing: "bg-paper/20 border-paper",
  delivered: "bg-ink text-bone border-ink",
  blocked:   "bg-red text-bone border-red"
};

const fmt = (ts: number) => {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return `${diff}s atrás`;
  if (diff < 3600) return `${Math.floor(diff/60)}min atrás`;
  return new Date(ts).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

export function OpsRoom() {
  const workers = useOps(s => s.workers);
  const events = useOps(s => s.events);
  const employees = useRH(s => s.employees);
  const tasks = useTasks(s => s.tasks);
  const [chatWith, setChatWith] = useState<string | null>(null);

  // Todos contratados, agrupados por depto
  const hired = PERSONAS.filter(p => employees[p.id]?.status === "hired");
  const byDept: Record<string, typeof hired> = {};
  hired.forEach(p => {
    const [d] = p.slotId.split(":");
    (byDept[d] ||= []).push(p);
  });

  const activeTasks = tasks.filter(t =>
    t.stage === "in-progress" || t.stage === "supervisor-review" || t.stage === "delegated");
  const busyCount = Object.values(workers).filter(w =>
    w.status !== "idle" && w.status !== "delivered").length;

  return (
    <div className="px-8 pb-16">
      <header className="border-2 border-ink bg-ink text-bone px-5 py-4 mb-4">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono text-xs text-gold">OPS-01</div>
            <h2 className="font-display font-black uppercase text-4xl leading-none">SALA DE OPERAÇÕES</h2>
            <div className="font-serif italic mt-1 text-bone/70">
              {hired.length} agentes no time · {busyCount} ativo{busyCount === 1 ? "" : "s"} agora · {activeTasks.length} demanda{activeTasks.length === 1 ? "" : "s"} em curso · <span className="text-gold">hover num agente pra 💬 conversar</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-right">
            <Pulse label="trabalhando" count={busyCount} color="bg-gold" />
            <Pulse label="em revisão" count={Object.values(workers).filter(w => w.status === "reviewing").length} color="bg-paper" />
            <Pulse label="entregue" count={Object.values(workers).filter(w => w.status === "delivered").length} color="bg-red" />
          </div>
        </div>
      </header>

      <div className="grid md:grid-cols-3 gap-3">
        {/* coluna 1+2: agentes por depto */}
        <div className="md:col-span-2 space-y-3">
          {Object.entries(byDept).map(([deptId, ppl]) => {
            const dept = ALL_DEPTS.find(d => d.id === deptId);
            return (
              <section key={deptId} className="border-2 border-ink bg-bone">
                <header className="bg-ink text-bone px-3 py-2 flex items-center justify-between">
                  <span className="font-display font-bold uppercase">{dept?.name ?? deptId}</span>
                  <span className="tag text-bone/70">{ppl.length}</span>
                </header>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 p-2">
                  {ppl.map(p => {
                    const w = workers[p.id];
                    const status = w?.status ?? "idle";
                    return (
                      <div key={p.id} className={`border-2 p-2 transition relative group ${STATUS_COLOR[status]}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-9 h-9 flex items-center justify-center font-display font-black text-xs shrink-0 overflow-hidden"
                               style={{ background: monogramColor(p.id), color: "#F2EEE6" }}>
                            {employees[p.id]?.avatarUrl
                              ? <img src={employees[p.id]!.avatarUrl} alt="" className="w-full h-full object-cover" />
                              : initials(p.name)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-bold uppercase text-[11px] leading-tight truncate">
                              {p.name}
                            </div>
                            <div className="tag text-[9px] opacity-75">{STATUS_LABEL[status]}</div>
                          </div>
                        </div>
                        {w?.detail && (
                          <div className="font-mono text-[9px] truncate opacity-80">→ {w.detail}</div>
                        )}
                        {w?.taskTitle && (
                          <div className="font-crimson italic text-[10px] truncate mt-0.5">{w.taskTitle}</div>
                        )}
                        <button onClick={() => setChatWith(p.id)}
                          title="💬 conversar com este agente"
                          className="absolute top-1 right-1 bg-ink text-bone text-[9px] px-1 py-0.5 opacity-0 group-hover:opacity-100 hover:bg-red">
                          💬 chat
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>

        {/* coluna 3: feed de eventos */}
        <aside className="border-2 border-ink bg-bone">
          <header className="bg-red text-bone px-3 py-2 flex items-center justify-between sticky top-0">
            <span className="font-display font-bold uppercase">📡 Live Feed</span>
            <span className="tag">{events.length}</span>
          </header>
          <div className="max-h-[70vh] overflow-y-auto p-2 space-y-1">
            {events.length === 0 ? (
              <div className="font-crimson italic text-paper text-sm p-3 text-center">
                nada acontecendo ainda. Rode um pipeline pra ver o time em ação.
              </div>
            ) : events.map(e => {
              const p = e.personaId ? PERSONAS.find(x => x.id === e.personaId) : null;
              return (
                <div key={e.id} className="border-l-2 border-red pl-2 py-0.5">
                  <div className="flex items-center gap-1 flex-wrap">
                    {p && (
                      <span className="inline-block w-4 h-4 text-[8px] font-display font-bold flex items-center justify-center text-bone"
                            style={{ background: monogramColor(p.id) }}>
                        {initials(p.name)}
                      </span>
                    )}
                    <span className="font-mono text-[10px] text-graydk">{fmt(e.at)}</span>
                  </div>
                  <div className="font-display text-[11px] leading-tight">{e.text}</div>
                </div>
              );
            })}
          </div>
        </aside>
      </div>

      {chatWith && <AgentChat initialAgentId={chatWith} onClose={() => setChatWith(null)} />}
    </div>
  );
}

function Pulse({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <div className="flex flex-col items-end">
      <div className="flex items-center gap-1">
        <span className={`inline-block w-2 h-2 ${color} ${count > 0 ? "animate-pulse" : "opacity-40"}`} />
        <span className="font-display font-black text-xl leading-none">{count}</span>
      </div>
      <div className="tag text-bone/60">{label}</div>
    </div>
  );
}
